import { useCallback, useMemo, useRef, useState } from 'react';
import {
  getBaseStatus,
  baseCapture,
  reserveBase,
  cancelBaseReservation
} from '../../../services/baseApi';
import { useCityInventory } from '../../city/hooks/useCityInventory';
import { deriveBaseCitiesFromCityConfig, BASE_SLOTS_PER_CITY } from '../constants';

function toMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value.seconds != null) return value.seconds * 1000;
  if (value._seconds != null) return value._seconds * 1000;
  if (typeof value === 'number') return value;
  return 0;
}

function normalizeStatus(statuses) {
  const byCity = {};
  for (const status of statuses || []) {
    if (!status?.cityId) continue;
    if (!byCity[status.cityId]) byCity[status.cityId] = [];
    byCity[status.cityId].push(status);
  }
  return byCity;
}

export default function useBaseManager({ piUser, environment, onTicketCountChange }) {
  const userId = piUser?.uid;
  const {
    configList,
    equippedList,
    owned,
    loading: cityLoading
  } = useCityInventory(userId, environment);

  const [statusData, setStatusData] = useState({
    statuses: [],
    myBases: [],
    currentBase: null,
    config: null,
    myReservation: null,
    myReservations: []
  });
  const [loading, setLoading] = useState(true);
  const [actionLoadingCityId, setActionLoadingCityId] = useState(null);
  const [error, setError] = useState(null);

  const baseCities = useMemo(
    () => deriveBaseCitiesFromCityConfig(configList),
    [configList]
  );

  // configList 변경 시 refresh deps 체인을 끊어 이중 호출 방지.
  // refresh 내부에서는 항상 최신 baseCities를 ref로 참조한다.
  const baseCitiesRef = useRef(baseCities);
  baseCitiesRef.current = baseCities;

  // 장착된 CITY ID 목록 (null 슬롯 제외)
  const equippedCityIds = useMemo(
    () => (equippedList || []).map((item) => item?.cityId).filter(Boolean),
    [equippedList]
  );

  const ownsGenesis = useMemo(
    () => (owned || []).some((item) => item?.cityId === 'city_genesis'),
    [owned]
  );

  // 슬롯 보너스(%) 계산 — Genesis +10%, 기타 +5%
  const slotBonusPercent = useMemo(
    () => equippedCityIds.reduce((sum, cId) => sum + (cId === 'city_genesis' ? 10 : 5), 0),
    [equippedCityIds]
  );

  const refresh = useCallback(async (options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const cityIds = baseCitiesRef.current.map((item) => item.id);
      const data = await getBaseStatus({
        userId,
        cityIds,
        environment,
        bypassCache: options.bypassCache === true
      });
      setStatusData({
        statuses: data.statuses || [],
        myBases: data.myBases || (data.currentBase ? [data.currentBase] : []),
        currentBase: data.currentBase || null,
        config: data.config || null,
        myReservation: data.myReservation || null,
        myReservations: Array.isArray(data.myReservations)
          ? data.myReservations
          : (data.myReservation ? [data.myReservation] : [])
      });
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [environment, userId]);

  // 자동 refresh는 SpotGoMain의 탭 진입 effect에서 제어한다.
  // (cityLoading 완료 시 자동 호출 + 탭 진입 시 재호출 = 중복 API 호출 방지)

  const statusesByCity = useMemo(
    () => normalizeStatus(statusData.statuses),
    [statusData.statuses]
  );

  const baseList = useMemo(() => {
    const slotsPerCity = statusData.config?.slotsPerCity || BASE_SLOTS_PER_CITY;
    const matchingCityBonusPercent = Math.round((statusData.config?.matchingCityBonus ?? 0.5) * 100); // 50

    return baseCities.map((city) => {
      const statuses = statusesByCity[city.id] || [];
      const activeStatuses = statuses.filter((s) => {
        const expiresAtMs = toMillis(s.expiresAt);
        const active = !expiresAtMs || expiresAtMs > Date.now();
        return active || !!s.reservedUserId;
      });

      const matchingCityEquipped = equippedCityIds.includes(city.id);
      const appliedMatchingBonus = matchingCityEquipped ? matchingCityBonusPercent : 0;
      const totalBonusPercent = slotBonusPercent + appliedMatchingBonus;

      // ownerId가 서버 응답에 포함되므로 직접 체크 (다중 BASE 지원)
      const isMyBase = userId
        ? activeStatuses.some((s) => s.ownerId === userId)
        : false;
      const myBaseStatus = isMyBase
        ? activeStatuses.find((s) => s.ownerId === userId) || null
        : null;

      return {
        ...city,
        type: 'base',
        cityId: city.id,
        occupiedCount: Math.min(slotsPerCity, activeStatuses.length),
        slotsPerCity,
        statuses: activeStatuses,
        matchingCityEquipped,
        slotBonusPercent,
        matchingBonusPercent: appliedMatchingBonus,
        totalBonusPercent,
        isMyBase,
        currentBase: myBaseStatus,
        isFull: activeStatuses.length >= slotsPerCity
      };
    });
  }, [baseCities, equippedCityIds, slotBonusPercent, statusData.config?.matchingCityBonus, statusData.config?.slotsPerCity, statusesByCity, userId]);

  const currentBase = statusData.currentBase;
  const myBases = statusData.myBases;
  const captureBase = useCallback(async (cityId) => {
    setActionLoadingCityId(cityId);
    setError(null);
    try {
      const data = await baseCapture({ piUser, userId, cityId, environment });
      // 티켓 수는 응답 데이터로 즉시 반영 (getCityInventory 재호출 불필요)
      if (typeof data?.base?.ticketCount === 'number') {
        onTicketCountChange?.(data.base.ticketCount);
      }
      // equippedCityIds는 점유 시 변하지 않으므로 refetchCityInventory 호출 생략
      // → cityLoading 토글이 발생하지 않아 SpotGoMain의 2번째 getBaseStatus 호출 방지
      await refresh({ bypassCache: true });
      return data;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setActionLoadingCityId(null);
    }
  }, [environment, onTicketCountChange, piUser, refresh, userId]);

  const reserveCity = useCallback(async (cityId) => {
    setActionLoadingCityId(cityId);
    setError(null);
    try {
      const data = await reserveBase({ piUser, userId, cityId, environment });
      if (typeof data?.reservation?.ticketCount === 'number') {
        onTicketCountChange?.(data.reservation.ticketCount);
      }
      await refresh({ bypassCache: true });
      return data;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setActionLoadingCityId(null);
    }
  }, [environment, onTicketCountChange, piUser, refresh, userId]);

  const cancelReservation = useCallback(async (cityId) => {
    setActionLoadingCityId(cityId || 'reserve');
    setError(null);
    try {
      const data = await cancelBaseReservation({ piUser, userId, cityId, environment });
      if (typeof data?.ticketCount === 'number') {
        onTicketCountChange?.(data.ticketCount);
      }
      await refresh({ bypassCache: true });
      return data;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setActionLoadingCityId(null);
    }
  }, [environment, onTicketCountChange, piUser, refresh, userId]);

  return {
    baseList,
    myBases,
    currentBase,
    myReservation: statusData.myReservation,
    myReservations: statusData.myReservations || [],
    ownsGenesis,
    config: statusData.config,
    loading: loading || cityLoading,
    cityLoading,
    error,
    actionLoadingCityId,
    refresh,
    captureBase,
    reserveCity,
    cancelReservation
  };
}
